/**
 * UserCard component - displays a single user with photo preview.
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Avatar,
  Typography,
  Button,
  Chip,
  Box,
  Skeleton,
} from '@mui/material';
import { Person, CheckCircle, Warning } from '@mui/icons-material';
import { UserPhotoStatus } from '../types';

interface UserCardProps {
  user: UserPhotoStatus;
  onManagePhotos: (user: UserPhotoStatus) => void;
  photoUrl: string | null;
  loadingPhoto: boolean;
}

const UserCard: React.FC<UserCardProps> = ({ user, onManagePhotos, photoUrl, loadingPhoto }) => {

  const getSyncStatusChip = () => {
    switch (user.sync_status) {
      case 'synced':
        return <Chip icon={<CheckCircle />} label="Synced" color="success" size="small" />;
      case 'out_of_sync':
        return <Chip icon={<Warning />} label="Out of Sync" color="warning" size="small" />;
      case 'never_synced':
        return null; // No chip for never_synced - neutral avatar makes it obvious
      default:
        return null;
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', maxWidth: 280 }}>
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          {loadingPhoto ? (
            <Skeleton variant="circular" width={80} height={80} />
          ) : (
            <Avatar
              src={photoUrl || undefined}
              sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
            >
              {!photoUrl && <Person sx={{ fontSize: 40 }} />}
            </Avatar>
          )}

          <Typography variant="subtitle2" component="div" align="center" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {user.display_name}
          </Typography>

          <Typography variant="caption" color="text.secondary" align="center" sx={{ lineHeight: 1.1 }}>
            {user.email}
          </Typography>

          {user.department && (
            <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: '0.7rem' }}>
              {user.department}
            </Typography>
          )}

          <Box mt={0.5}>
            {getSyncStatusChip()}
          </Box>

          <Box display="flex" gap={1.5} mt={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Graph: {user.has_graph_photo ? '✓' : '✗'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              SP: {user.sharepoint_photo_count}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onManagePhotos(user)}
          sx={{ fontSize: '0.75rem', py: 0.5 }}
          fullWidth
        >
          Manage
        </Button>
      </CardActions>
    </Card>
  );
};

export default UserCard;
